// Guest flow, step 3 of 3 before the request form itself: organization +
// contact details. Only reachable after verify succeeds (pages/GuestPage.jsx
// owns that gating). contactEmail is carried forward read-only from step 1
// — never editable here without restarting verification (this task's
// brief).
//
// idPrefix/withPassword (customer-accounts task): reused by
// pages/RegisterPage.jsx with idPrefix='registerDetails' and
// withPassword — registration needs the identical org/contact fields PLUS
// a password (it creates a real login-capable account, unlike the guest
// flow's identical-looking details step). One component instead of two
// near-duplicates — portal/CLAUDE.md's 120KB budget makes that reuse worth
// the extra prop. Password/confirmPassword are deliberately never mirrored
// to the onDraftChange callback (a credential must not survive a reload the
// way every other field does — see pages/RegisterPage.jsx's own
// usePersistedState draft).

import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useLanguage } from '../context/LanguageContext.jsx';
import { PasswordInput } from 'shared/components/PasswordInput/index.js';
import { Spinner } from 'shared/components/Feedback/index.js';
import styles from './GuestDetailsStep.module.css';

export default function GuestDetailsStep({
  email,
  defaultValues,
  onDraftChange,
  onContinue,
  idPrefix = 'guestDetails',
  withPassword = false,
  isSubmitting = false,
}) {
  const { t, lang } = useLanguage();
  const { register, control, handleSubmit, watch, formState } = useForm({
    defaultValues: {
      organizationName: defaultValues?.organizationName || '',
      tin: defaultValues?.tin || '',
      contactName: defaultValues?.contactName || '',
      contactPhone: defaultValues?.contactPhone || '',
      address: defaultValues?.address || '',
      password: '',
      confirmPassword: '',
    },
  });
  const watched = useWatch({ control });
  const password = watch('password');

  useEffect(() => {
    const { password: _password, confirmPassword: _confirmPassword, ...rest } = watched;
    onDraftChange?.(withPassword ? rest : watched);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify({ ...watched, password: undefined, confirmPassword: undefined })]);

  const submit = handleSubmit((values) => {
    onContinue({
      organizationName: values.organizationName.trim() || undefined,
      tin: values.tin.trim() || undefined,
      contactName: values.contactName.trim(),
      contactPhone: values.contactPhone.trim() || undefined,
      address: values.address.trim() || undefined,
      ...(withPassword ? { password: values.password } : {}),
    });
  });

  return (
    <form className={styles.form} onSubmit={submit} noValidate>
      <div className={styles.intro}>
        <h1 className={styles.title}>{t(`portal.${idPrefix}.title`)}</h1>
        <p className={styles.subtitle}>{t(`portal.${idPrefix}.subtitle`)}</p>
      </div>

      <div className={styles.grid}>
        <label className={`${styles.field} ${styles.fieldFull}`}>
          <span className={styles.label}>{t(`portal.${idPrefix}.emailLabel`)}</span>
          <input type="email" className={styles.input} value={email} readOnly disabled />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>{t(`portal.${idPrefix}.contactName`)}</span>
          <input
            type="text"
            className={styles.input}
            maxLength={120}
            {...register('contactName', { required: true })}
          />
          {formState.errors.contactName && (
            <span className={styles.fieldError}>{t('error.VALIDATION_ERROR')}</span>
          )}
        </label>

        <label className={styles.field}>
          <span className={styles.label}>{t(`portal.${idPrefix}.contactPhone`)}</span>
          <input
            type="tel"
            inputMode="tel"
            className={styles.input}
            maxLength={20}
            {...register('contactPhone')}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>{t(`portal.${idPrefix}.organizationNameOptional`)}</span>
          <input
            type="text"
            className={styles.input}
            maxLength={200}
            {...register('organizationName')}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>{t(`portal.${idPrefix}.tin`)}</span>
          <input type="text" className={styles.input} maxLength={20} {...register('tin')} />
        </label>

        <label className={`${styles.field} ${styles.fieldFull}`}>
          <span className={styles.label}>{t(`portal.${idPrefix}.address`)}</span>
          <input type="text" className={styles.input} maxLength={255} {...register('address')} />
        </label>

        {withPassword && (
          <>
            <label className={styles.field}>
              <span className={styles.label}>{t('portal.registerDetails.password')}</span>
              <PasswordInput
                lang={lang}
                autoComplete="new-password"
                {...register('password', { required: true, minLength: 8, maxLength: 200 })}
              />
              {formState.errors.password && (
                <span className={styles.fieldError}>
                  {t('portal.registerDetails.passwordTooShort')}
                </span>
              )}
            </label>

            <label className={styles.field}>
              <span className={styles.label}>{t('portal.registerDetails.confirmPassword')}</span>
              <PasswordInput
                lang={lang}
                autoComplete="new-password"
                {...register('confirmPassword', {
                  required: true,
                  validate: (value) => value === password,
                })}
              />
              {formState.errors.confirmPassword && (
                <span className={styles.fieldError}>
                  {t('portal.registerDetails.passwordMismatch')}
                </span>
              )}
            </label>
          </>
        )}
      </div>

      <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
        {isSubmitting && <Spinner />} {t(`portal.${idPrefix}.continue`)}
      </button>
    </form>
  );
}
